import React, { useEffect } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from '@mui/material/Grid';
import Typography from "@mui/material/Typography";
import { useWebServer } from "../../../providers";
import _ from "lodash";
import { Place } from "../../../types";

interface Props {
  handleSelect: (address?: Place) => void;
  handleInputChange: (address?: string) => void;
  initialValue?: string;
}

const MapAutoComplete: React.FC<Props> = ({
  initialValue = null,
  handleInputChange,
  handleSelect,
}) => {
  const [value, setValue] = React.useState<null | string | Place>(
    initialValue || null
  );
  const [options, setOptions] = React.useState<readonly Place[]>([]);

  useEffect(() => {
    // Only set the initial value if it's different from the current value
    if (initialValue !== value && initialValue !== undefined) {
      setValue(initialValue);
    }
  }, [initialValue, value]);

  const { getGPSLocationFromAddress } = useWebServer();

  // Create the debounced function
  const debouncedGetGPSLocationFromAddress = React.useMemo(
    () =>
      _.debounce(
        async (address: string): Promise<Place[] | []> => {
          return await getGPSLocationFromAddress(address);
        },
        500,
        { trailing: true }
      ),
    [getGPSLocationFromAddress]
  );

  // Wrap the debounced function in a Promise for proper async/await handling
  const searchAddress = React.useCallback(
    (address: string): Promise<Place[] | []> => {
      return new Promise<Place[] | []>((resolve, reject) => {
        const promise = debouncedGetGPSLocationFromAddress(address);
        if (promise) {
          promise
            .then((result: Place[] | []) => {
              resolve(result);
            })
            .catch(reject);
        } else {
          resolve([]);
        }
      });
    },
    [debouncedGetGPSLocationFromAddress]
  );

  // Example handler to test
  const handleAddressSearch = async (address: string) => {
    const result: Place[] = await searchAddress(address);
    setOptions(result);
  };

  return (
    <Autocomplete
      fullWidth
      getOptionLabel={(option: string | Place) =>
        typeof option === "string" ? option : option.display_name || ""
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      freeSolo
      value={value}
      noOptionsText="No valid locations"
      onChange={(_, newValue: string | Place | null) => {
        setValue(newValue);
        handleSelect(newValue as Place);
      }}
      onInputChange={async (_, newInputValue) => {
        if (handleInputChange) {
          handleInputChange(newInputValue);
        }
        await handleAddressSearch(newInputValue);
      }}
      renderInput={(params) => <TextField {...params} fullWidth />}
      renderOption={(props, option: string | Place) => {
        const { key, ...optionProps } = props;
        return (
          <li
            key={
              typeof option === "string" ? option : option.display_name || ""
            }
            {...optionProps}
          >
            <Grid container sx={{ alignItems: "center" }}>
              <Grid
                sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {typeof option === "string"
                    ? option
                    : option.display_name || ""}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
};

export default MapAutoComplete;
